import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/packages', label: 'Packages' },
  { to: '/contact', label: 'Contact' },
];

export const Header = ({ transparentOnTop = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!transparentOnTop) {
      setScrolled(true);
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [transparentOnTop]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const showSolid = scrolled || mobileOpen;

  return (
    <header
      data-testid="marketing-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showSolid
          ? 'bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="marketing-logo"
          className="flex items-center"
        >
          <img
            src={showSolid ? '/marketing/brand/logoMain.png' : '/marketing/brand/logoMainWhite.png'}
            alt="Rate My Service"
            className="h-10 lg:h-11 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-full transition ${
                  isActive
                    ? showSolid
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-white bg-white/15'
                    : showSolid
                    ? 'text-slate-700 hover:text-blue-600 hover:bg-slate-100'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Link
            to="/login"
            data-testid="header-login-link"
            className={`px-4 py-2 text-sm font-semibold rounded-full transition ${
              showSolid
                ? 'text-slate-700 hover:text-blue-600'
                : 'text-white/90 hover:text-white'
            }`}
          >
            Sign in
          </Link>
          <Link
            to="/register"
            data-testid="header-register-button"
            className="px-5 py-2.5 text-sm font-semibold rounded-full bg-blue-600 text-white hover:bg-blue-700 shadow-sm shadow-blue-600/30 transition"
          >
            Get Started
          </Link>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className={`md:hidden p-2 rounded-md ${
            showSolid ? 'text-slate-900' : 'text-white'
          }`}
          aria-label="Toggle menu"
        >
          {mobileOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
          )}
        </button>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-white border-t border-slate-200">
          <div className="px-6 py-4 flex flex-col gap-2">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-base font-medium ${
                    isActive ? 'text-blue-600 bg-blue-50' : 'text-slate-700 hover:bg-slate-100'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-3 mt-2 border-t border-slate-200 flex gap-2">
              <Link to="/login" data-testid="mobile-login-link" className="flex-1 text-center px-4 py-2.5 rounded-full text-sm font-semibold text-slate-700 border border-slate-300">Sign in</Link>
              <Link to="/register" data-testid="mobile-register-button" className="flex-1 text-center px-4 py-2.5 rounded-full text-sm font-semibold bg-blue-600 text-white">Get Started</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
